import {
	Alert,
	Box,
	Button,
	Divider,
	Icons,
	Inline,
	Stack,
	Text,
} from "@sembark-travel/ui-base"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import { ErrorBoundary } from "@sembark-travel/ui-error-boundary"
import {
	CheckboxInputField,
	ErrorMessage,
	FileInputField,
	Form,
	GetFieldValue,
	SubmissionError,
	TextAreaInputField,
	useField,
	useFormState,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { ReactNode, useEffect, useState } from "react"
import { boolean, InferType, mixed, object, string } from "yup"
import { useXHR } from "@sembark-travel/xhr"
import { useNavigate } from "../router-utils"

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10 MB

const supportedFormats = [
	"application/pdf",
	"image/png",
	"image/jpeg",
	"image/webp",
	"application/msword",
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
	"message/rfc822", // .eml
]

const UploadPackageSchema = object({
	content: string().optional(),

	file: mixed<File>()
		.optional()
		.test(
			"file",
			"Unsupported file type",
			(file) =>
				!file ||
				supportedFormats.includes(file.type) ||
				file.name.toLowerCase().endsWith(".eml")
		),

	ai_acknowledged: boolean().oneOf(
		[true],
		"Please acknowledge that AI-generated content may contain mistakes."
	),
}).test(
	"content-or-file",
	"Please provide either email content or upload a file.",
	function (value) {
		const hasContent = !!value?.content?.trim()
		const hasFile = value?.file instanceof File

		if (hasContent || hasFile) {
			return true
		}

		return this.createError({
			path: "file",
			message: "Please provide either email content or upload a file.",
		})
	}
)

type IUploadPackage = InferType<typeof UploadPackageSchema>

export default function UploadPackageDialog({
	children,
	tripId,
}: {
	children: (props: { onClick: () => void }) => ReactNode
	tripId: number
}) {
	const [isOpen, open, close] = useDialog()

	return (
		<>
			{children({ onClick: () => open() })}
			<Dialog md title="Upload with AI" onClose={() => close()} open={isOpen}>
				<ErrorBoundary>
					<UploadPackage onCancel={close} tripId={tripId} />
				</ErrorBoundary>
			</Dialog>
		</>
	)
}

function UploadPackage({
	tripId,
	onCancel,
}: {
	tripId: number
	onCancel: () => void
}) {
	const xhr = useXHR()
	const navigate = useNavigate()

	return (
		<>
			<Dialog.Body>
				<Form<IUploadPackage>
					initialValues={{
						content: "",
						ai_acknowledged: false,
					}}
					validate={validateFormValues(UploadPackageSchema)}
					onSubmit={withServerErrors(async (values) => {
						const data = new FormData()
						if (values.file) {
							data.set("file", values.file)
						}

						data.set("content", values.content?.trim() ?? "")
						const res = await xhr.post(
							`/trips/${tripId}/upload-package-for-parsing`,
							data,
							{
								headers: {
									"Content-Type": "multipart/form-data",
								},
							}
						)
						const jobUuid = res?.data?.job_uuid

						if (!jobUuid) {
							throw new Error("Failed to upload Package. Please try again.")
						}

						navigate(
							{
								pathname: "/trips/:tripId/new-quote",
								search: `?job_uuid=${res?.data?.job_uuid}`,
							},
							{
								params: {
									tripId: tripId.toString(),
								},
							}
						)
					})}
					subscription={{ submitting: true }}
				>
					{({ handleSubmit, submitting, form: { resetFieldState } }) => {
						return (
							<form noValidate onSubmit={handleSubmit}>
								<Stack gap={"3"}>
									<GetFieldValue name="file">
										{({ value }) => (
											<TextAreaInputField
												disabled={!!value}
												rows={4}
												type="textarea"
												name="content"
												label="Paste Content"
												placeholder="Paste package content here"
											/>
										)}
									</GetFieldValue>

									<Inline alignItems={"center"} gap={"2"}>
										<Divider width={"full"} />
										<Box
											as="span"
											fontSize={"base"}
											color={"subtle"}
											fontWeight="semibold"
										>
											or
										</Box>
										<Divider width={"full"} />
									</Inline>

									<UploadPreview
										clearError={(type: keyof IUploadPackage) =>
											resetFieldState(type)
										}
									/>

									<GetFieldValue name="ai_acknowledged">
										{({ value }) => (
											<>
												<Alert
													title="Before You Upload"
													status={value ? "success" : "warning"}
													type="polite"
												>
													<Stack
														gap={"2"}
														as="ul"
														listStyleType="disc"
														marginLeft="4"
													>
														<Text as="li" fontSize={"base"}>
															AI-generated responses may contain errors. Please
															verify important information before sharing it
															with your customers.
														</Text>

														<Text fontSize={"base"} as="li">
															Ensure the destination(s) in your quote match the
															destination(s) in the uploaded document or pasted
															content.
														</Text>
													</Stack>
													<Divider
														marginY={"2"}
														borderColor={value ? "success" : "warning"}
													/>
													<Box>
														<CheckboxInputField
															name="ai_acknowledged"
															type="checkbox"
															label={
																"I understand that AI-generated results may contain mistakes and I will review the parsed information before sharing it with customers."
															}
															hideErrors
														/>
													</Box>
												</Alert>
											</>
										)}
									</GetFieldValue>

									<ErrorMessage name="ai_acknowledged" />

									<SubmissionError />
								</Stack>

								<Divider sm width={"full"} />

								<Inline gap="4">
									<Button disabled={submitting} type="submit" level="primary">
										{submitting ? "Uploading...." : " Upload"}
									</Button>
									<Button
										onClick={() => onCancel()}
										disabled={submitting}
										level="tertiary"
									>
										Cancel
									</Button>
								</Inline>
							</form>
						)
					}}
				</Form>
			</Dialog.Body>
		</>
	)
}

function UploadPreview({
	clearError,
}: {
	clearError?: (type: keyof IUploadPackage) => void
}) {
	const { input } = useField<File | undefined>("file", {
		subscription: {
			value: true,
		},
	})

	const formState = useFormState({
		subscription: {
			submitError: true,
			dirtySinceLastSubmit: true,
		},
	})
	const showError = formState.submitError && !formState.dirtySinceLastSubmit
	const file = input.value

	const [preview, setPreview] = useState<string>()

	function handleChangeFile(files: FileList | null) {
		const file = files?.[0]

		if (!file) {
			input.onChange(undefined)
			return
		}

		if (file.size > MAX_FILE_SIZE) {
			alert("File size must not exceed 10 MB.")
			input.onChange(undefined)
			return
		}

		input.onChange(file)
	}

	useEffect(() => {
		if (!(file instanceof File)) {
			setPreview(undefined)
			return
		}

		const url = URL.createObjectURL(file)
		setPreview(url)

		return () => URL.revokeObjectURL(url)
	}, [file])

	// No file selected
	if (!(file instanceof File)) {
		return (
			<GetFieldValue<IUploadPackage["content"]> name="content">
				{({ value }) => {
					return (
						<FileInputField
							label="Select Document"
							name="file"
							width="full"
							disabled={!!value?.trim()}
							accept="image/*,application/pdf,.doc,.docx,.eml"
							onChange={handleChangeFile}
							help={
								<Text>
									File must be DOC, DOCX, PDF or Image and less than 10 MB.
								</Text>
							}
						/>
					)
				}}
			</GetFieldValue>
		)
	}

	return (
		<Stack gap="3">
			<Box
				gap={"2"}
				display={"flex"}
				justifyContent={"between"}
				alignItems={"center"}
			>
				{file.name && (
					<Text fontSize={"base"} fontWeight="semibold">
						{file.name}
					</Text>
				)}
				<Button
					level="tertiary"
					onClick={() => {
						clearError?.("file")
						input.onChange(undefined)
					}}
				>
					<Icons.Refresh /> Change File
				</Button>
			</Box>

			<Stack padding="3" alignItems="center">
				{/* Image Preview */}
				{file.type.startsWith("image/") && preview && (
					<img
						src={preview}
						alt={file.name}
						style={{
							maxWidth: "100%",
							maxHeight: 300,
							objectFit: "contain",
						}}
					/>
				)}

				{/* PDF Preview */}
				{file.type === "application/pdf" && preview && (
					<iframe src={preview} title={file.name} width="100%" height="400" />
				)}

				{(file.name.endsWith(".doc") || file.name.endsWith(".docx")) && (
					<Box textAlign="center">
						<Text fontWeight="semibold">{file.name}</Text>
						<Text color="warning">
							Preview is not available for Word documents.
						</Text>
					</Box>
				)}
			</Stack>

			{showError && <ErrorMessage name="file" />}
		</Stack>
	)
}
